import type { SerpMarket } from '@seoplum/core'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './api'

export type Me = Awaited<ReturnType<typeof fetchMe>>
export type Organization = Me['organizations'][number]
export type Site = Awaited<ReturnType<typeof fetchSites>>['sites'][number]

async function fetchMe() {
  const response = await api.api.me.$get()
  return unwrap<{
    user: { id: string; name: string; email: string; image: string | null }
    activeOrganizationId: string
    role: string
    /** Whether to render the admin link. The admin routes prove it themselves;
     * this only decides whether the door is visible. */
    isStaff: boolean
    siteCount: number
    connectionCount: number
    organizations: { id: string; name: string; slug: string; role: string }[]
  }>(response)
}

async function fetchSites() {
  const response = await api.api.sites.$get()
  return unwrap<{
    sites: {
      id: string
      host: string
      displayName: string
      /** Set when this host is covered by another site's property. */
      parentSiteId: string | null
      createdAt: string
      propertyId: string | null
      propertyUri: string | null
      propertyType: 'url-prefix' | 'sc-domain' | null
      permissionLevel: string | null
      searchTypes: string[] | null
      linkedAt: string | null
      connectionId: string | null
      connectionEmail: string | null
      connectionInvalidatedAt: string | null
      lastSyncedAt: string | null
      searchConsole: 'connected' | 'not-connected' | 'needs-reconnect'
      /** Where this site's rankings are read. Always resolved: a site that has
       * never picked one comes back carrying the deployment's default rather
       * than a null the caller would have to know how to fill. */
      serpMarket: SerpMarket
    }[]
  }>(response)
}

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: fetchMe,
  // The shell reads this on every route; a short stale window keeps navigation
  // instant without serving a stale workspace after switching.
  staleTime: 30_000,
  retry: false,
})

export const sitesQuery = queryOptions({
  queryKey: ['sites'],
  queryFn: fetchSites,
  staleTime: 10_000,
})

/**
 * Segments the router owns at the position an org slug or a host would sit. A
 * literal route always out-ranks a param route, so anything in this list would
 * be silently shadowed; the routes reject it up front instead.
 */
export const RESERVED_SEGMENTS = [
  'admin',
  'api',
  'connect',
  'design',
  'invite',
  'login',
  'new',
  'settings',
  'signup',
  'sites',
] as const

export function isReservedSegment(segment: string) {
  return (RESERVED_SEGMENTS as readonly string[]).includes(segment.toLowerCase())
}

const LAST_WORKSPACE_KEY = 'seoplum:last-workspace'

/** `/` carries no tenant, so it sends you back to the one you were last in. */
export function rememberWorkspace(slug: string) {
  try {
    localStorage.setItem(LAST_WORKSPACE_KEY, slug)
  } catch {
    // Private mode or a full quota: the fallback is the first workspace.
  }
}

export function readRememberedWorkspace() {
  try {
    return localStorage.getItem(LAST_WORKSPACE_KEY)
  } catch {
    return null
  }
}

/**
 * Where a URL with no tenant in it should land: the workspace you were last in,
 * then whatever the session still calls active, then the first membership.
 */
export function resolveWorkspace(me: Me) {
  const remembered = readRememberedWorkspace()
  return (
    me.organizations.find((organization) => organization.slug === remembered) ??
    me.organizations.find((organization) => organization.id === me.activeOrganizationId) ??
    me.organizations[0]
  )
}

export function findOrganization(me: Me | undefined, slug: string | undefined) {
  if (!me || !slug) return undefined
  return me.organizations.find((organization) => organization.slug === slug)
}

export function findSite(sites: Site[] | undefined, host: string | undefined) {
  if (!sites || !host) return undefined
  const wanted = host.toLowerCase()
  return sites.find((site) => site.host.toLowerCase() === wanted)
}

export type SiteNode = { site: Site; depth: number }

/**
 * A parent property's numbers already contain its subdomains', so children are
 * rendered under their parent rather than beside it — side by side the totals
 * read as double counting.
 */
export function nestSites(sites: Site[]): SiteNode[] {
  const byId = new Map(sites.map((site) => [site.id, site]))
  const children = new Map<string, Site[]>()
  const roots: Site[] = []

  for (const site of sites) {
    const parentId = site.parentSiteId
    if (parentId && byId.has(parentId)) {
      const siblings = children.get(parentId)
      if (siblings) siblings.push(site)
      else children.set(parentId, [site])
    } else {
      roots.push(site)
    }
  }

  const nodes: SiteNode[] = []
  const walk = (site: Site, depth: number) => {
    nodes.push({ site, depth })
    for (const child of children.get(site.id) ?? []) walk(child, depth + 1)
  }
  for (const root of roots) walk(root, 0)
  return nodes
}

export const DATE_RANGES = ['7d', '28d', '3m', '12m', '16m'] as const
export type DateRange = (typeof DATE_RANGES)[number]
export const DEFAULT_DATE_RANGE: DateRange = '28d'

export function parseDateRange(value: unknown): DateRange | undefined {
  return typeof value === 'string' && (DATE_RANGES as readonly string[]).includes(value)
    ? (value as DateRange)
    : undefined
}

export function useCreateSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { domain: string; displayName?: string }) => {
      const response = await api.api.sites.$post({ json: input })
      return unwrap<{ site: { id: string; host: string; displayName: string } }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useRenameSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, displayName }: { id: string; displayName: string }) => {
      const response = await api.api.sites[':id'].$patch({
        param: { id },
        json: { displayName },
      })
      return unwrap<{ site: { id: string } }>(response)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] }),
  })
}

/**
 * The market this site is read in.
 *
 * Its own hook rather than a second argument to the rename, because the two
 * edits have nothing to do with each other and a shared mutation would make
 * every rename resend a market and every market change resend a name.
 *
 * It invalidates the SERP queries as well as the sites list: the tracked list
 * and every keyword page default to this market, so they are answering a
 * different question the moment it changes.
 */
export function useSetSiteMarket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, market }: { id: string; market: SerpMarket }) => {
      const response = await api.api.sites[':id'].$patch({
        param: { id },
        json: { serpMarket: market },
      })
      return unwrap<{ site: { id: string } }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      queryClient.invalidateQueries({ queryKey: ['serp'] })
    },
  })
}

export function useDeleteSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.api.sites[':id'].$delete({ param: { id } })
      return unwrap<{ id: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export type Connection = Awaited<ReturnType<typeof fetchConnections>>['connections'][number]
export type PropertyGroup = Awaited<ReturnType<typeof fetchProperties>>['groups'][number]

async function fetchConnections() {
  const response = await api.api.connections.$get()
  return unwrap<{
    connections: {
      id: string
      googleEmail: string
      scopes: string[]
      connectedBy: string | null
      connectedByName: string | null
      lastSyncedAt: string | null
      propertiesSyncedAt: string | null
      invalidatedAt: string | null
      invalidatedReason: string | null
      createdAt: string
      propertyCount: number
      siteCount: number
    }[]
  }>(response)
}

export const connectionsQuery = queryOptions({
  queryKey: ['connections'],
  queryFn: fetchConnections,
  staleTime: 10_000,
})

async function fetchProperties(connectionId: string) {
  const response = await api.api.connections[':id'].properties.$get({
    param: { id: connectionId },
  })
  return unwrap<{
    connection: {
      id: string
      googleEmail: string
      propertiesSyncedAt: string | null
      invalidatedAt: string | null
    }
    groups: {
      host: string
      /** Set when another property in this account already covers this host. */
      parentHost: string | null
      readable: boolean
      suggestedPrimary: { id: string; propertyUri: string; propertyType: string } | null
      properties: {
        id: string
        propertyUri: string
        propertyType: 'url-prefix' | 'sc-domain'
        permissionLevel: string
      }[]
      existingSiteId: string | null
      alreadyLinked: boolean
    }[]
  }>(response)
}

export function propertiesQuery(connectionId: string) {
  return queryOptions({
    queryKey: ['connections', connectionId, 'properties'],
    queryFn: () => fetchProperties(connectionId),
    // Discovery runs as a background job right after OAuth, so the first read
    // can legitimately come back empty. Polling here is what turns that into
    // "finding your properties" instead of "you have none".
    refetchInterval: (query) => ((query.state.data?.groups.length ?? 0) === 0 ? 2000 : false),
  })
}

/** Hands the browser to Google. The API signs the workspace into the state
 * parameter, so the callback knows the tenant without trusting the URL. */
export function useStartGoogleConnect() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.api.connections.google.start.$post()
      return unwrap<{ authUrl: string }>(response)
    },
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl
    },
  })
}

export function useRefreshProperties() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const before = (await fetchProperties(connectionId)).connection
      const response = await api.api.connections[':id'].properties.refresh.$post({
        param: { id: connectionId },
      })
      await unwrap<{ queued: boolean }>(response)
      // The api only queues discovery, so invalidating on the 202 re-reads the
      // list the worker has not rewritten yet. Wait for the job to stamp the
      // connection — synced, or invalidated by a dead token — before refetching.
      const deadline = Date.now() + 30_000
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        const { connection } = await fetchProperties(connectionId)
        if (
          connection.propertiesSyncedAt !== before.propertiesSyncedAt ||
          connection.invalidatedAt !== before.invalidatedAt
        ) {
          return
        }
      }
    },
    onSuccess: (_result, connectionId) => {
      queryClient.invalidateQueries({ queryKey: ['connections', connectionId, 'properties'] })
      queryClient.invalidateQueries({ queryKey: ['connections'] })
    },
  })
}

export function useDisconnectGoogle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const response = await api.api.connections[':id'].$delete({ param: { id: connectionId } })
      return unwrap<{ id: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export type BingConnection = Awaited<ReturnType<typeof fetchBingConnections>>['connections'][number]

async function fetchBingConnections() {
  const response = await api.api.connections.bing.$get()
  return unwrap<{
    connections: {
      id: string
      keyHint: string
      connectedByName: string | null
      sitesSyncedAt: string | null
      invalidatedAt: string | null
      invalidatedReason: string | null
      createdAt: string
      siteCount: number
      linkedCount: number
    }[]
  }>(response)
}

export const bingConnectionsQuery = queryOptions({
  queryKey: ['connections', 'bing'],
  queryFn: fetchBingConnections,
  staleTime: 10_000,
})

export type BingSiteStatus = 'linked' | 'linked-elsewhere' | 'pending' | 'unverified' | 'no-site'

async function fetchBingSites(connectionId: string) {
  const response = await api.api.connections.bing[':id'].sites.$get({
    param: { id: connectionId },
  })
  return unwrap<{
    connection: { id: string; sitesSyncedAt: string | null }
    sites: { id: string; siteUrl: string; host: string; status: BingSiteStatus }[]
  }>(response)
}

export function bingSitesQuery(connectionId: string) {
  return queryOptions({
    queryKey: ['connections', 'bing', connectionId, 'sites'],
    queryFn: () => fetchBingSites(connectionId),
  })
}

/** Same bargain as `useRefreshProperties`: the api only queues, so wait for the
 * worker to stamp the connection before re-reading. */
export function useRefreshBingSites() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const before = (await fetchBingSites(connectionId)).connection
      const response = await api.api.connections.bing[':id'].refresh.$post({
        param: { id: connectionId },
      })
      await unwrap<{ queued: boolean }>(response)
      const deadline = Date.now() + 30_000
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        const { connection } = await fetchBingSites(connectionId)
        if (connection.sitesSyncedAt !== before.sitesSyncedAt) return
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections', 'bing'] })
    },
  })
}

export function useAddBingKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (apiKey: string) => {
      const response = await api.api.connections.bing.$post({ json: { apiKey } })
      return unwrap<{ id: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections', 'bing'] })
    },
  })
}

export function useRemoveBingKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.api.connections.bing[':id'].$delete({ param: { id } })
      return unwrap<{ id: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections', 'bing'] })
      queryClient.invalidateQueries({ queryKey: ['site-data'] })
    },
  })
}

export interface PropertySelection {
  host: string
  primaryPropertyId: string
  secondaryPropertyIds?: string[]
}

export function useSelectProperties(connectionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (selections: PropertySelection[]) => {
      const response = await api.api.connections[':id'].properties.select.$post({
        param: { id: connectionId },
        json: { selections },
      })
      return unwrap<{ sites: { id: string; host: string; created: boolean }[] }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}
